import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, resolveImg, getToken, clearToken } from "../../api";
import HomeSlidesEditor from "./HomeSlidesEditor";
import MessagesInbox from "./MessagesInbox";
import OrdersInbox from "./OrdersInbox";
import InfoEditor from "./InfoEditor";
import SubscribersInbox from "./SubscribersInbox";
import SyncTool from "./SyncTool";

const SECTIONS = [
  {
    key: "home",
    label: "Home",
    fields: [],
  },
  {
    key: "info",
    label: "Info",
    fields: [],
  },
  {
    key: "messages",
    label: "Messages",
    fields: [],
  },
  {
    key: "subscribers",
    label: "Subscribers",
    fields: [],
  },
  {
    key: "orders",
    label: "Orders",
    fields: [],
  },
  {
    key: "sync",
    label: "Sync",
    fields: [],
  },
  {
    key: "projects",
    label: "Projects",
    fields: [
      { name: "title", type: "text", label: "Title" },
      { name: "year", type: "text", label: "Year" },
      { name: "status", type: "text", label: "Status (e.g. ongoing)" },
      { name: "category", type: "text", label: "Category" },
      { name: "description", type: "textarea", label: "Description" },
    ],
  },
  {
    key: "commercial",
    label: "Commercial",
    fields: [
      { name: "client", type: "text", label: "Client" },
      { name: "title", type: "text", label: "Title" },
      { name: "year", type: "number", label: "Year" },
      { name: "category", type: "text", label: "Category" },
      { name: "description", type: "textarea", label: "Description" },
      { name: "credits", type: "textarea", label: "Credits" },
      { name: "externalUrl", type: "text", label: "External URL" },
    ],
  },
  {
    key: "exhibitions",
    label: "Exhibitions",
    fields: [
      { name: "title", type: "text", label: "Project / Title" },
      { name: "kind", type: "text", label: "Kind (Solo, Group, Festival...)" },
      { name: "dates", type: "text", label: "Dates" },
      { name: "venue", type: "text", label: "Venue" },
      { name: "city", type: "text", label: "City" },
      { name: "description", type: "textarea", label: "Description" },
    ],
  },
  {
    key: "films",
    label: "Films",
    fields: [
      { name: "title", type: "text", label: "Title" },
      { name: "subtitle", type: "text", label: "Subtitle" },
      { name: "year", type: "number", label: "Year" },
      { name: "duration", type: "text", label: "Duration" },
      { name: "director", type: "text", label: "Director" },
      { name: "youtubeId", type: "text", label: "YouTube video ID (e.g. dQw4w9WgXcQ)" },
      { name: "externalUrl", type: "text", label: "External URL (fallback)" },
      { name: "description", type: "textarea", label: "Description" },
    ],
  },
  {
    key: "shop_products",
    label: "Shop",
    fields: [
      { name: "title", type: "text", label: "Title" },
      { name: "subtitle", type: "text", label: "Subtitle" },
      { name: "author", type: "text", label: "Author" },
      { name: "year", type: "number", label: "Year" },
      { name: "price", type: "number", label: "Price (EUR)" },
      { name: "stock", type: "number", label: "Stock" },
      { name: "description", type: "textarea", label: "Description" },
    ],
  },
];

const emptyItem = (section) => {
  const item = {};
  section.fields.forEach((f) => {
    item[f.name] = f.type === "number" ? 0 : "";
  });
  item.cover = "";
  item.images = [];
  return item;
};

const ImageBox = ({ url, onRemove, onSetCover, isCover, index, onDragStart, onDragOver, onDrop, isDragging }) => (
  <div
    className={`relative group aspect-square bg-[color:var(--paper-2)] overflow-hidden cursor-move ${isDragging ? "opacity-40" : ""}`}
    draggable
    onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; onDragStart(index); }}
    onDragOver={(e) => { e.preventDefault(); onDragOver(index); }}
    onDrop={(e) => { e.preventDefault(); onDrop(index); }}
  >
    <img src={resolveImg(url)} alt="" className="w-full h-full object-cover pointer-events-none" />
    <span className="absolute top-1 right-1 bg-white/95 text-[10px] px-1.5 py-0.5 font-serif counter">{index + 1}</span>
    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 pointer-events-none">
      <div className="pointer-events-auto flex flex-col items-center gap-1.5">
        {!isCover && (
          <button type="button" onClick={onSetCover} className="text-white text-xs font-serif underline">Set as cover</button>
        )}
        <button type="button" onClick={onRemove} className="text-white text-xs font-serif underline">Remove</button>
        <span className="text-white/70 text-[10px] font-serif mt-1">drag to reorder</span>
      </div>
    </div>
    {isCover && (
      <span className="absolute top-1 left-1 bg-white/95 text-[10px] px-1.5 py-0.5 font-serif">Cover</span>
    )}
  </div>
);

const ItemEditor = ({ section, item, onChange, onUpload, uploading }) => {
  const [dragIdx, setDragIdx] = React.useState(null);
  const [overIdx, setOverIdx] = React.useState(null);

  const handleDrop = (target) => {
    if (dragIdx === null || dragIdx === target) { setDragIdx(null); setOverIdx(null); return; }
    const arr = [...(item.images || [])];
    const [moved] = arr.splice(dragIdx, 1);
    arr.splice(target, 0, moved);
    onChange({ ...item, images: arr });
    setDragIdx(null);
    setOverIdx(null);
  };

  return (
  <div className="space-y-4">
    {section.fields.map((f) => (
      <label key={f.name} className="block">
        <span className="font-serif text-[13px] text-[color:var(--ink-mute)]">{f.label}</span>
        {f.type === "textarea" ? (
          <textarea
            rows={3}
            value={item[f.name] || ""}
            onChange={(e) => onChange({ ...item, [f.name]: e.target.value })}
            className="mt-1 w-full bg-transparent border border-[color:var(--line)] p-2 outline-none font-serif text-[14px] resize-none"
          />
        ) : (
          <input
            type={f.type}
            value={item[f.name] ?? ""}
            onChange={(e) => onChange({ ...item, [f.name]: f.type === "number" ? Number(e.target.value) : e.target.value })}
            className="mt-1 w-full bg-transparent border-b border-[color:var(--ink)] py-1.5 outline-none font-serif text-[14px]"
          />
        )}
      </label>
    ))}

    <div>
      <div className="flex items-baseline justify-between mb-2">
        <span className="font-serif text-[13px] text-[color:var(--ink-mute)]">
          Images ({(item.images || []).length}) <span className="text-[color:var(--ink-mute)]">— drag to reorder</span>
        </span>
        <label className="font-serif text-[13px] tlink cursor-pointer">
          + Add images
          <input type="file" accept="image/*" multiple hidden onChange={(e) => { onUpload(Array.from(e.target.files)); e.target.value = ""; }} />
        </label>
      </div>
      {uploading && <p className="font-serif text-[12px] text-[color:var(--ink-mute)] mb-2">Uploading…</p>}
      <div className="grid grid-cols-4 gap-2">
        {(item.images || []).map((img, idx) => (
          <div
            key={`${img}-${idx}`}
            className={overIdx === idx && dragIdx !== idx ? "ring-2 ring-[color:var(--ink)]" : ""}
          >
            <ImageBox
              url={img}
              index={idx}
              isCover={img === item.cover}
              isDragging={dragIdx === idx}
              onDragStart={setDragIdx}
              onDragOver={setOverIdx}
              onDrop={handleDrop}
              onSetCover={() => onChange({ ...item, cover: img })}
              onRemove={() => onChange({
                ...item,
                images: item.images.filter((_, i) => i !== idx),
                cover: item.cover === img ? (item.images.filter((_, i) => i !== idx)[0] || "") : item.cover,
              })}
            />
          </div>
        ))}
      </div>
      {(item.images || []).length === 0 && (
        <p className="font-serif text-[13px] text-[color:var(--ink-mute)] italic mt-2">No images yet — click "+ Add images" to upload.</p>
      )}
    </div>
  </div>
  );
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeKey, setActiveKey] = useState("projects");
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const section = SECTIONS.find((s) => s.key === activeKey);
  const isHome = activeKey === "home";
  const isInfo = activeKey === "info";
  const isMessages = activeKey === "messages";
  const isSubscribers = activeKey === "subscribers";
  const isOrders = activeKey === "orders";
  const isSync = activeKey === "sync";
  const isSpecial = isHome || isInfo || isMessages || isSubscribers || isOrders || isSync;

  const load = useCallback(async () => {
    try {
      await api.me();
    } catch {
      clearToken();
      navigate("/admin/login");
      return;
    }
    if (isSpecial) { setItems([]); setEditing(null); return; }
    const list = await api.list(activeKey);
    setItems(list);
    setEditing(null);
  }, [activeKey, navigate, isSpecial]);

  useEffect(() => { if (!getToken()) navigate("/admin/login"); else load(); }, [load, navigate]);

  const onUpload = async (files) => {
    if (!editing) return;
    setUploading(true);
    try {
      const results = [];
      for (const f of files) {
        const r = await api.upload(f);
        results.push(r.url);
      }
      const nextImages = [...(editing.images || []), ...results];
      setEditing({
        ...editing,
        images: nextImages,
        cover: editing.cover || results[0] || nextImages[0] || "",
      });
    } catch (e) {
      alert("Upload failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setUploading(false);
    }
  };

  const onSave = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const isNew = !items.find((i) => i.id === editing.id);
      if (isNew) {
        await api.create(activeKey, editing);
      } else {
        await api.update(activeKey, editing.id, editing);
      }
      await load();
    } catch (e) {
      alert("Save failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!editing || !editing.id) return;
    if (!confirm(`Delete "${editing.title || editing.client || editing.id}"?`)) return;
    try {
      await api.remove(activeKey, editing.id);
      await load();
    } catch (e) {
      alert("Delete failed");
    }
  };

  const moveItem = async (index, direction) => {
    const arr = [...items];
    const target = index + direction;
    if (target < 0 || target >= arr.length) return;
    [arr[index], arr[target]] = [arr[target], arr[index]];
    setItems(arr);
    await api.reorder(activeKey, arr.map((i) => i.id));
  };

  const logout = () => { clearToken(); navigate("/admin/login"); };

  return (
    <div className="min-h-screen bg-[color:var(--paper)]">
      {/* Top bar */}
      <header className="px-6 md:px-10 py-4 border-b border-[color:var(--line)] flex items-center justify-between bg-[color:var(--paper)]">
        <div className="flex items-baseline gap-4">
          <span className="brand-mark">Alice Lisi</span>
          <span className="font-serif text-[13px] text-[color:var(--ink-mute)]">Admin</span>
        </div>
        <div className="flex items-center gap-4">
          <a href="/" target="_blank" rel="noreferrer" className="font-serif text-[13px] tlink-mute">View site →</a>
          <button onClick={logout} className="font-serif text-[13px] tlink-mute">Log out</button>
        </div>
      </header>

      {/* Tabs */}
      <div className="px-6 md:px-10 py-4 border-b border-[color:var(--line)] flex flex-wrap gap-4">
        {SECTIONS.map((s) => (
          <button
            key={s.key}
            onClick={() => setActiveKey(s.key)}
            className={`font-serif text-[14px] tlink ${activeKey === s.key ? "tlink-active" : "tlink-mute"}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-12 min-h-[80vh]">
        {isHome ? (
          <section className="col-span-12">
            <HomeSlidesEditor />
          </section>
        ) : isInfo ? (
          <section className="col-span-12">
            <InfoEditor />
          </section>
        ) : isMessages ? (
          <section className="col-span-12">
            <MessagesInbox />
          </section>
        ) : isSubscribers ? (
          <section className="col-span-12">
            <SubscribersInbox />
          </section>
        ) : isOrders ? (
          <section className="col-span-12">
            <OrdersInbox />
          </section>
        ) : isSync ? (
          <section className="col-span-12">
            <SyncTool />
          </section>
        ) : (
        <>
        {/* Left: list */}
        <aside className="col-span-12 md:col-span-4 border-r border-[color:var(--line)] p-5 space-y-2">
          <button
            onClick={() => setEditing(emptyItem(section))}
            className="btn-sober-outline w-full justify-center mb-4"
          >
            + New {section.label.slice(0, -1)}
          </button>

          {items.map((it, i) => (
            <div key={it.id} className={`group flex items-center gap-2 p-2 rounded border ${editing?.id === it.id ? "border-[color:var(--ink)] bg-[color:var(--paper-2)]" : "border-transparent hover:bg-[color:var(--paper-2)]"}`}>
              {it.cover && <img src={resolveImg(it.cover)} alt="" className="w-10 h-10 object-cover" />}
              <button onClick={() => setEditing({ ...it })} className="flex-1 text-left">
                <p className="font-serif text-[14px] leading-tight">{it.title || it.client || "Untitled"}</p>
                <p className="font-serif text-[11px] text-[color:var(--ink-mute)]">{it.category || it.kind || it.subtitle || ""} {it.year && `— ${it.year}`}</p>
              </button>
              <div className="flex flex-col opacity-0 group-hover:opacity-100">
                <button onClick={() => moveItem(i, -1)} className="text-[10px] px-1" title="Move up">▲</button>
                <button onClick={() => moveItem(i, 1)} className="text-[10px] px-1" title="Move down">▼</button>
              </div>
            </div>
          ))}
        </aside>

        {/* Right: editor */}
        <section className="col-span-12 md:col-span-8 p-6 md:p-8">
          {!editing ? (
            <div className="text-center text-[color:var(--ink-mute)] font-serif py-20">
              Select an item from the left, or click <em>+ New</em> to create one.
            </div>
          ) : (
            <div className="max-w-2xl">
              <div className="flex items-baseline justify-between mb-6">
                <p className="font-serif text-2xl">{editing.title || editing.client || "New item"}</p>
                <p className="font-serif text-[12px] text-[color:var(--ink-mute)]">{editing.id ? `id: ${editing.id}` : "new"}</p>
              </div>

              <ItemEditor section={section} item={editing} onChange={setEditing} onUpload={onUpload} uploading={uploading} />

              <div className="mt-8 flex items-center gap-3">
                <button onClick={onSave} disabled={saving} className="btn-sober">{saving ? "Saving…" : "Save"}</button>
                {editing.id && items.find((i) => i.id === editing.id) && (
                  <button onClick={onDelete} className="font-serif text-[13px] text-red-600 tlink">Delete</button>
                )}
                <button onClick={() => setEditing(null)} className="font-serif text-[13px] tlink-mute">Cancel</button>
              </div>
            </div>
          )}
        </section>
        </>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
